// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Input, Select, Textarea, FormActions } from '@/components/ui'
import { useZone, useCreateZone, useUpdateZone } from '@/hooks/useParkingZones'
import { useProperty } from '@/hooks/useProperties'
import { useModuleEnabled } from '@/hooks/useModuleEnabled'
import { PublicCheckInStickerCard } from './PublicCheckInStickerCard'
import { ParkingFeeTiersSection } from './ParkingFeeTiersSection'

interface ZoneFormData {
  name: string
  zone_type: 'car' | 'motorcycle' | 'mixed'
  total_slots: number
  notes: string
}

export function ParkingZoneFormPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const { propertyId, id } = useParams<{ propertyId?: string; id?: string }>()
  const isEditing = !!id
  const zoneId = Number(id)
  const navigate = useNavigate()

  // When editing, propertyId comes from the zone data; when creating, from the route
  const { data: existingZone, isLoading: isLoadingZone } = useZone(isEditing ? zoneId : 0)
  const createZone = useCreateZone()
  const updateZone = useUpdateZone()

  // Resolve propertyId: from route param (new) or from loaded zone (edit)
  const resolvedPropertyId = propertyId
    ? Number(propertyId)
    : existingZone?.property_id ?? 0

  const { data: property } = useProperty(resolvedPropertyId)
  // Both cards below are kiosk affordances that only make sense with the
  // module on: a kiosk link/sticker for an account with parking_fee_enabled
  // off answers 403 to every visitor. `=== true` because the hook is
  // undefined while account settings are loading, so the cards stay hidden
  // rather than flashing in and out (same idiom as PropertyDetailPage's
  // showVisitorWidget).
  const parkingFeeModuleEnabled = useModuleEnabled('parkingFee') === true

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ZoneFormData>({
    defaultValues: {
      name: '',
      zone_type: 'car',
      total_slots: 1,
      notes: '',
    },
  })

  // Pre-populate form when editing
  useEffect(() => {
    if (isEditing && existingZone) {
      reset({
        name: existingZone.name,
        zone_type: existingZone.zone_type,
        total_slots: existingZone.total_slots,
        notes: existingZone.notes ?? '',
      })
    }
  }, [isEditing, existingZone, reset])

  const zoneTypeOptions = [
    { value: 'car', label: t('zones.types.car') },
    { value: 'motorcycle', label: t('zones.types.motorcycle') },
    { value: 'mixed', label: t('zones.types.mixed') },
  ]

  const onSubmit = async (data: ZoneFormData) => {
    if (isEditing) {
      await updateZone.mutateAsync({
        id: zoneId,
        name: data.name,
        zone_type: data.zone_type,
        total_slots: Number(data.total_slots),
        notes: data.notes || undefined,
      })
      navigate(`/properties/${existingZone?.property_id}/parking/zones`)
    } else {
      await createZone.mutateAsync({
        property_id: Number(propertyId),
        name: data.name,
        zone_type: data.zone_type,
        total_slots: Number(data.total_slots),
        notes: data.notes || undefined,
      })
      navigate(`/properties/${propertyId}/parking/zones`)
    }
  }

  if (isEditing && isLoadingZone) {
    return (
        <div className="space-y-6">
          <Card>
            <div className="h-64 animate-pulse bg-base-200 rounded" />
          </Card>
        </div>
    )
  }

  return (
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            {
              label: property?.name ?? '...',
              href: `/properties/${resolvedPropertyId}`,
            },
            {
              label: t('zones.title'),
              href: `/properties/${resolvedPropertyId}/parking/zones`,
            },
            { label: isEditing ? t('zones.edit') : t('zones.new_zone') },
          ]}
        />

        <PageHeader
          title={isEditing ? t('zones.edit') : t('zones.new_zone')}
        />

        <Card>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label={t('zones.name')}
                placeholder={t('zones.name')}
                error={errors.name?.message}
                {...register('name', { required: tc('errors.required') })}
              />

              <Select
                label={t('zones.zone_type')}
                options={zoneTypeOptions}
                error={errors.zone_type?.message}
                {...register('zone_type', { required: tc('errors.required') })}
              />

              <Input
                label={t('zones.total_slots')}
                type="number"
                min={1}
                error={errors.total_slots?.message}
                {...register('total_slots', {
                  required: tc('errors.required'),
                  min: {
                    value: 1,
                    message: t('validation.total_slots_min'),
                  },
                  valueAsNumber: true,
                })}
              />
            </div>

            <Textarea
              label={t('labels.notes')}
              rows={3}
              {...register('notes')}
            />

            <FormActions>
              <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
                {tc('actions.cancel')}
              </Button>
              <Button
                type="submit"
                loading={isEditing ? updateZone.isPending : createZone.isPending}
              >
                {isEditing ? tc('actions.save') : tc('actions.create')}
              </Button>
            </FormActions>
          </form>
        </Card>

        {/* Public self check-in sticker, admin-only. The backend serializes
            public_check_in_key only for admins, so its presence gates the card.
            Also gated on the parking fee module: a kiosk link is useless (403)
            with the module off. */}
        {isEditing && existingZone && 'public_check_in_key' in existingZone && parkingFeeModuleEnabled && (
          <PublicCheckInStickerCard zone={existingZone} />
        )}

        {/* EP-105 hourly-tier fee editor, admin-only (the backend serializes
            session_fee_formula only for admins), also gated on the parking
            fee module for the same reason as the sticker card above. */}
        {isEditing && existingZone && 'public_check_in_key' in existingZone && parkingFeeModuleEnabled && (
          <ParkingFeeTiersSection
            zoneId={existingZone.id}
            initialFormula={existingZone.session_fee_formula}
          />
        )}
      </div>
  )
}
